/**
 * The Flight operations look, shared by its screens: a blue apron behind the
 * page, a live strip with the clock and what needs looking at now, the ramp's
 * colours as pills — red when a departure's doors are late, yellow while it
 * boards, orange when delayed — and the knock-on of delays on stands and gates.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { OPS_LABEL, impactSentence, opsState, type Impact, type OpsState } from "../../domain/flightDelays";
import type { Flight } from "../../domain/flights";
import { ErrorState, Skeleton } from "../../design/components";
import { t } from "../../i18n";
import s from "./Flights.module.css";

export function FlightTheme({ children }: { children: ReactNode }) {
  return (
    <div className={s.theme} data-testid="flight-theme">
      {children}
    </div>
  );
}

export function OpsPill({ state }: { state: OpsState }) {
  return (
    <span className={s.pill} data-ops={state} data-testid="ops-pill">
      {t(OPS_LABEL[state])}
    </span>
  );
}

export function OpsLegend() {
  return (
    <div className={s.legend} aria-label={t("What the colors mean")}>
      {(["door_late", "boarding", "delayed", "on_time", "scheduled", "departed"] as OpsState[]).map((st) => (
        <OpsPill key={st} state={st} />
      ))}
    </div>
  );
}

function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** The clock and what needs looking at now, each a link to where it is dealt with. */
export function LiveStrip({ flights, site }: { flights: readonly Flight[]; site: string }) {
  const now = useClock();
  const live = flights.filter((f) => f.source !== "forecast");
  const count = (st: OpsState) => live.filter((f) => opsState(f, now) === st).length;
  const doors = count("door_late");
  const boarding = count("boarding");
  const delayed = count("delayed");
  const toCome = live.filter((f) => !f.actualAt && !f.cancelled).length;
  const stats: { key: string; n: number; label: string; c: string; to: string; pulse?: boolean }[] = [
    { key: "door", n: doors, label: "doors late", c: "var(--ops-red)", to: "/flights?show=doors", pulse: doors > 0 },
    { key: "boarding", n: boarding, label: "boarding now", c: "var(--ops-yellow)", to: "/fids" },
    { key: "delayed", n: delayed, label: "delayed", c: "var(--ops-orange)", to: "/flight-delays" },
    { key: "tocome", n: toCome, label: "still to come", c: "var(--ops-blue)", to: "/flights?show=notyet" },
  ];
  return (
    <section className={s.live} data-testid="flight-live">
      <div className={s.liveClock}>
        {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        <small>
          {site} · {t("local time")}
        </small>
      </div>
      <div className={s.liveStats}>
        {stats.map((x) => (
          <Link
            key={x.key}
            to={x.to}
            className={s.liveStat}
            style={{ ["--c" as string]: x.c }}
            data-pulse={x.pulse ?? false}
            data-testid={`live-${x.key}`}
          >
            <b>{x.n}</b>
            {t(x.label)}
          </Link>
        ))}
      </div>
    </section>
  );
}

export function ImpactList({ impacts, empty }: { impacts: Impact[]; empty: string }) {
  if (!impacts.length) return <p className={s.meta}>✓ {t(empty)}</p>;
  return (
    <div style={{ display: "grid", gap: 8 }} data-testid="impacts">
      {impacts.map((i, n) => (
        <div key={n} className={s.impact} data-testid={`impact-${i.kind}-${i.where}`}>
          <span className={s.impactWhere}>
            {i.kind === "stand" ? "🅿" : "🚪"} {i.where}
          </span>
          <span style={{ flex: 1, minWidth: 220 }}>
            {impactSentence(i)}
            {i.suggestion && (
              <div className={s.fix}>
                →{" "}
                {t("Move {victim} to stand {stand}: {because}.", {
                  victim: i.victim,
                  stand: i.suggestion.stand,
                  because: i.suggestion.because,
                })}
              </div>
            )}
            {i.kind === "stand" && !i.suggestion && (
              <div className={s.bad}>{t("No free stand fits; hold the arrival or re-plan the apron.")}</div>
            )}
            {i.kind === "gate" && (
              <div className={s.meta}>{t("Give one of them another gate, or bus from a remote stand.")}</div>
            )}
          </span>
          <Link to="/stands">{t("Stands and gates")} →</Link>
        </div>
      ))}
    </div>
  );
}

/** What a flight screen needs to know about the day it reads, from `useFlightWorld`. */
export interface DayRead {
  flights: readonly Flight[];
  serviceDate: string;
  loading: boolean;
  error: string | null;
  planError?: string | null;
  stale?: string | null;
  day?: unknown;
}

/** True when there is a day to draw: read, without error, with flights on it. */
export const hasDay = (w: DayRead) => !w.loading && !w.error && w.flights.length > 0;

/**
 * What is missing from a screen that is drawn: the stand plan that could not
 * be read (so no sizes, gates or stands), or a refresh that failed (so the
 * screen shows the last good read).
 */
export function DayNotes({ world }: { world: DayRead }) {
  if (world.loading) return null;
  return (
    <>
      {world.planError && (
        <p className={s.note} data-testid="flights-plan-error">
          {t("The stand plan could not be read, so stands, gates and sizes are not shown: {why}", { why: world.planError })}
        </p>
      )}
      {world.stale && (
        <p className={s.note} data-testid="flights-stale">
          {t("Could not refresh the flight record, so this is the last good read: {why}", { why: world.stale })}
        </p>
      )}
    </>
  );
}

/**
 * The day's state where there is nothing to draw yet: a placeholder while it
 * loads (never the empty line, which would flash), the platform's message if
 * the read failed, or one plain line when the record has no flights for the
 * day — pointing at the flight record, where the schedule is added. A screen
 * that is nothing without the stand plan passes `needsPlan`.
 */
export function DayState({ world, needsPlan }: { world: DayRead; needsPlan?: boolean }) {
  if (world.loading) return <Skeleton rows={3} />;
  if (world.error) return <ErrorState error={new Error(world.error)} />;
  if (needsPlan && !world.day && world.planError) return <ErrorState error={new Error(world.planError)} />;
  if (world.flights.length > 0) return <DayNotes world={world} />;
  return (
    <>
      <DayNotes world={world} />
      <p className={s.note} data-testid="flights-empty-day">
        {t("No flights on the record for {date}.", { date: world.serviceDate })}{" "}
        <Link to="/aodb">{t("Add the schedule")} →</Link>
      </p>
    </>
  );
}
